import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, Download, ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const FamilyPage = () => {
  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 md:pt-40 pb-10 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Users size={14} />
            <span>The Family</span>
          </div>
          <h1
            data-testid="family-title"
            className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
          >
            <span className="text-red-500">One Game.</span>
            <br />
            <span className="text-amber-400">Cultural Unity.</span>
            <br />
            <span className="text-green-500">One Family.</span>
          </h1>
          <p className="text-neutral-400 max-w-xl mt-6 font-light leading-relaxed">
            Eight athletes. Eight stories. One uniform. The official AfroAm
            family poster — a celebration of the cultural bridge we're building
            on and off the court.
          </p>
        </div>
      </section>

      {/* Poster */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="relative border border-amber-400/30"
          >
            <img
              src="/posters/team-unity.png"
              alt="AfroAm Basketball Association — One Game. Cultural Unity. One Family."
              className="w-full h-auto object-contain"
              data-testid="family-poster"
            />
            <span className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-amber-400" />
            <span className="absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 border-amber-400" />
            <span className="absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-amber-400" />
            <span className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-amber-400" />
          </motion.div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-[10px] uppercase tracking-[0.35em] text-neutral-500">
            <span>Inaugural Campaign · 2026</span>
            <a
              href="/posters/team-unity.png"
              download="afroam-family-poster.png"
              data-testid="family-download"
              className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300"
            >
              <Download size={12} />
              Download Poster
            </a>
          </div>
        </div>
      </section>

      {/* Meaning strip */}
      <section className="border-t border-neutral-900 py-16 md:py-24 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900">
            {[
              {
                color: "text-red-500",
                title: "One Game",
                body: "Basketball is the common language — from blacktops in Houston to courts in Lagos.",
              },
              {
                color: "text-amber-400",
                title: "Cultural Unity",
                body: "Pan-African heritage meets African American excellence. Patterns, palette, pride.",
              },
              {
                color: "text-green-500",
                title: "One Family",
                body: "Every athlete is signing on to something larger than the sport — a diaspora moving together.",
              },
            ].map((m) => (
              <div key={m.title} className="bg-black p-8 md:p-10">
                <div className={`font-display uppercase text-3xl ${m.color}`}>
                  {m.title}
                </div>
                <p className="text-neutral-400 font-light leading-relaxed mt-4 text-sm">
                  {m.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-4 items-center justify-between">
            <div className="text-neutral-400 max-w-xl">
              Want to join the family in front of the lens or behind the
              mission?
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/voices"
                data-testid="family-voices-cta"
                className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
              >
                Read the Voices
              </Link>
              <Link
                to="/#contact"
                data-testid="family-join-cta"
                className="inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
              >
                Join the Movement
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FamilyPage;
